import { useEffect, useMemo, useRef, useState } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { useGameStore } from "./useGameStore";
import {
  preloadTracksSequentially,
  releasePreloadedTracks,
  useTrack,
} from "../audio/useTrack";
import { preloadSlashSounds } from "../audio/useSlashSound";
import { Block } from "./Block";
import { Obstacle } from "./Obstacle";
import type { Note, ObstacleNote } from "./beatmap";
import { TRAVEL_TIME } from "./constants";
import { getTargetTravelTime, SURVIVAL_TARGET_DEFAULTS } from "./targetSystem";
import {
  SURVIVAL_MISS_LIMIT,
  SURVIVAL_TRACKS,
  createPausableClock,
  difficultyForIntensity,
  generateSurvivalBeatEvents,
  generateSurvivalNote,
  survivalIntensity,
  trackIndexForStage,
  type SurvivalBeatEvent,
  type SurvivalCompositionState,
} from "./survival";

// Survival reuses the campaign target (<Block>) verbatim — same rendering,
// collision, grading, tenouchi/zanshin, trail and slice. The ONLY difference
// from the campaign is the note source: procedurally generated here instead of
// authored JSON. Block already calls addMiss() on a miss/wrong-direction, which
// feeds the miss counter this mode's fail condition watches.
interface ActiveNote extends Note {
  id: number;
  spawnCamY: number;
  scheduledSpawnTime: number;
}

interface ActiveObstacle extends ObstacleNote {
  id: number;
}

const MAX_ACTIVE_OBSTACLES = 2;
// Mirrors GameManager's cap — the difficulty ramp keeps steady-state
// concurrency well under this; it's a safety net, not a tuning knob.
const MAX_CONCURRENT_NOTES = 10;
const SCHEDULE_LOOKAHEAD_SECONDS = 4;
const PROGRESS_UPDATE_SECONDS = 0.2;
const TRACK_SWITCH_LOOKAHEAD_SECONDS = 0.05;
const SURVIVAL_TRACK_SOURCES = SURVIVAL_TRACKS.map((track) => track.src);

function createSeededRandom(seed: number): () => number {
  let value = seed >>> 0;
  return () => {
    value = (value * 1664525 + 1013904223) >>> 0;
    return value / 0x1_0000_0000;
  };
}

export function SurvivalManager() {
  const phase = useGameStore((state) => state.phase);
  const missCount = useGameStore((state) => state.missCount);
  const audioStatus = useGameStore((state) => state.survivalAudioStatus);
  const [trackStage, setTrackStage] = useState(0);
  const [activeNotes, setActiveNotes] = useState<ActiveNote[]>([]);
  const [activeObstacles, setActiveObstacles] = useState<ActiveObstacle[]>([]);
  const { camera } = useThree();

  const clock = useMemo(() => createPausableClock(), []);
  const beatCursor = useRef(0);
  const schedule = useRef<SurvivalBeatEvent[]>([]);
  const noteId = useRef(0);
  const obstacleId = useRef(0);
  const lastComposition = useRef<SurvivalCompositionState | null>(null);
  const random = useRef(createSeededRandom(0x5a4d5552));
  const lastProgressUpdate = useRef(-Infinity);
  const transitionPending = useRef(false);
  const completed = useRef(false);
  const mounted = useRef(true);
  const preloadAbort = useRef<AbortController | null>(null);
  const previousPhase = useRef(phase);
  const previousTrackId = useRef<string | null>(null);

  const trackIndex = trackIndexForStage(trackStage);
  const track = SURVIVAL_TRACKS[trackIndex];
  const {
    play,
    stop,
    pause,
    resume,
    getCurrentTime: getTrackTime,
  } = useTrack(audioStatus === "ready" ? track.src : null, {
    fallbackOnPlayError: true,
  });

  useEffect(() => {
    return () => {
      mounted.current = false;
      preloadAbort.current?.abort();
      releasePreloadedTracks(SURVIVAL_TRACK_SOURCES);
    };
  }, []);

  useEffect(() => {
    if (phase !== "survival-intro" || audioStatus !== "idle") return;

    const store = useGameStore.getState();
    const abortController = new AbortController();
    preloadAbort.current = abortController;
    store.setSurvivalAudioProgress("loading", 0, SURVIVAL_TRACK_SOURCES.length);
    void preloadTracksSequentially(
      SURVIVAL_TRACK_SOURCES,
      (loaded, total) => {
        if (!mounted.current) return;
        useGameStore
          .getState()
          .setSurvivalAudioProgress("loading", loaded, total);
      },
      abortController.signal,
    )
      .then(() => {
        if (!mounted.current) return;
        useGameStore
          .getState()
          .setSurvivalAudioProgress(
            "ready",
            SURVIVAL_TRACK_SOURCES.length,
            SURVIVAL_TRACK_SOURCES.length,
          );
      })
      .catch((error) => {
        if (error instanceof DOMException && error.name === "AbortError")
          return;
        console.warn("Survival audio failed to preload.", error);
        if (!mounted.current) return;
        useGameStore
          .getState()
          .setSurvivalAudioProgress("error", 0, SURVIVAL_TRACK_SOURCES.length);
      })
      .finally(() => {
        if (preloadAbort.current === abortController) {
          preloadAbort.current = null;
        }
        if (!mounted.current) releasePreloadedTracks(SURVIVAL_TRACK_SOURCES);
      });
  }, [audioStatus, phase]);

  useEffect(() => {
    const previous = previousPhase.current;
    const trackChanged = previousTrackId.current !== track.id;
    previousPhase.current = phase;
    previousTrackId.current = track.id;

    if (phase === "countdown" || phase === "playing") {
      preloadSlashSounds().catch(() => undefined);
    }

    if (phase === "playing") {
      if (previous === "paused" && !trackChanged) {
        clock.resume();
        resume();
        return;
      }

      if (previous !== "playing") {
        clock.start();
        setTrackStage(0);
        noteId.current = 0;
        obstacleId.current = 0;
        lastComposition.current = null;
        random.current = createSeededRandom(Date.now());
        lastProgressUpdate.current = -Infinity;
        completed.current = false;
        setActiveNotes([]);
        setActiveObstacles([]);
      }

      if (trackChanged && previous === "playing") {
        setActiveNotes([]);
        setActiveObstacles([]);
        lastComposition.current = null;
      }

      beatCursor.current = 0;
      schedule.current = [];
      transitionPending.current = false;
      play(
        trackChanged && previous === "playing"
          ? TRACK_SWITCH_LOOKAHEAD_SECONDS
          : 0,
      );
      return;
    }

    if (phase === "paused") {
      clock.pause();
      pause();
      return;
    }

    if (
      phase === "menu" ||
      phase === "survival-intro" ||
      phase === "fail" ||
      phase === "win"
    ) {
      stop();
      schedule.current = [];
      setActiveNotes([]);
      setActiveObstacles([]);
    }
  }, [phase, track.id]);

  useEffect(() => {
    if (
      phase !== "playing" ||
      completed.current ||
      missCount < SURVIVAL_MISS_LIMIT
    ) {
      return;
    }

    completed.current = true;
    const elapsed = clock.now();
    const score = useGameStore.getState().score;
    useGameStore.getState().setSurvivalProgress({
      elapsed,
      trackIndex,
      intensity: survivalIntensity(elapsed, score),
    });
    useGameStore.getState().completeSurvivalRun();
  }, [missCount, phase, trackIndex]);

  function fillSchedule(audioTime: number, intensity: number) {
    const difficulty = difficultyForIntensity(intensity);
    const beats = track.rhythm.beats;

    while (
      beatCursor.current < beats.length &&
      beats[beatCursor.current] <= audioTime + SCHEDULE_LOOKAHEAD_SECONDS
    ) {
      const index = beatCursor.current;
      const beatTime = beats[index];
      beatCursor.current += 1;

      // The standard blue profile has the longest travel time. Fast orange
      // targets can be mounted early without entering the visible lane early.
      const leadTime = getTargetTravelTime(difficulty.speed);
      if (beatTime < leadTime + 0.2) continue;

      schedule.current.push(
        ...generateSurvivalBeatEvents(beats, index, difficulty, random.current),
      );
    }
  }

  useFrame(() => {
    if (phase !== "playing" || completed.current) return;

    const elapsed = clock.now();
    const store = useGameStore.getState();
    const score = store.score;
    const intensity = survivalIntensity(elapsed, score);
    const desiredStage = Math.floor(intensity);

    if (desiredStage > trackStage && !transitionPending.current) {
      transitionPending.current = true;
      setTrackStage((current) => current + 1);
      return;
    }
    if (transitionPending.current) return;

    if (elapsed - lastProgressUpdate.current >= PROGRESS_UPDATE_SECONDS) {
      lastProgressUpdate.current = elapsed;
      store.setSurvivalProgress({ elapsed, trackIndex, intensity });
    }

    const audioTime = getTrackTime();
    fillSchedule(audioTime, intensity);
    let obstaclesSpawnedThisFrame = 0;
    const toSpawn: ActiveNote[] = [];

    while (schedule.current.length > 0) {
      const event = schedule.current[0];
      const difficulty = difficultyForIntensity(intensity);
      const travelTime =
        event.kind === "target"
          ? getTargetTravelTime(difficulty.speed)
          : TRAVEL_TIME;
      if (event.trackTime - travelTime > audioTime) break;

      if (event.kind === "obstacle") {
        if (
          activeObstacles.length + obstaclesSpawnedThisFrame >=
          MAX_ACTIVE_OBSTACLES
        ) {
          break;
        }
        const globalHitTime = elapsed + (event.trackTime - audioTime);
        setActiveObstacles((current) => [
          ...current,
          {
            id: obstacleId.current++,
            t: globalHitTime,
            type: event.type,
          },
        ]);
        obstaclesSpawnedThisFrame += 1;
        schedule.current.shift();
        continue;
      }

      if (activeNotes.length + toSpawn.length >= MAX_CONCURRENT_NOTES) break;

      const globalHitTime = elapsed + (event.trackTime - audioTime);
      const generated = generateSurvivalNote(
        noteId.current,
        globalHitTime,
        difficulty,
        lastComposition.current,
        random.current,
      );
      const scheduledSpawnTime =
        globalHitTime -
        getTargetTravelTime(generated.note.speed ?? difficulty.speed);
      toSpawn.push({
        ...generated.note,
        id: noteId.current,
        spawnCamY: camera.position.y,
        scheduledSpawnTime,
      });

      noteId.current += 1;
      lastComposition.current = generated.composition;
      schedule.current.shift();
    }

    if (toSpawn.length > 0) {
      setActiveNotes((current) => [...current, ...toSpawn]);
    }
  });

  if (phase !== "playing" && phase !== "paused") return null;

  return (
    <>
      {activeNotes.map((note) => (
        <Block
          key={`survival-note-${note.id}`}
          note={note}
          noteId={note.id}
          spawnCamY={note.spawnCamY}
          scheduledSpawnTime={note.scheduledSpawnTime}
          targetDefaults={SURVIVAL_TARGET_DEFAULTS}
          getCurrentTime={() => clock.now()}
          onRemove={() =>
            setActiveNotes((current) =>
              current.filter((item) => item.id !== note.id),
            )
          }
        />
      ))}
      {activeObstacles.map((obstacle) => (
        <Obstacle
          key={`survival-obstacle-${obstacle.id}`}
          note={obstacle}
          obstacleId={obstacle.id}
          getCurrentTime={() => clock.now()}
          onRemove={() =>
            setActiveObstacles((current) =>
              current.filter((item) => item.id !== obstacle.id),
            )
          }
        />
      ))}
    </>
  );
}
